
import Vue, { PropOptions } from 'vue';

// A treatment carries its parent product so that selecting it records both the
// treatment and the product ownership for the location (MAINTAIN-1715). Because a
// treatment can sit under more than one product, the same treatment can be checked
// independently under each product it belongs to.
interface TreeTreatment {
  id: number;
  title: string;
}
interface TreeProduct {
  id: number;
  title: string;
  treatments: TreeTreatment[];
}
interface Taxonomy {
  id: number;
  title: string;
}

export default Vue.extend({
  name: 'FormTreatmentSelectionControl',
  props: {
    // Owned products for the practice, each with their treatments.
    tree: {
      type: Array,
      default: () => [],
    } as PropOptions<TreeProduct[]>,
    // The location's currently-selected treatments.
    selectedTreatments: {
      type: Array,
      default: () => [],
    } as PropOptions<Taxonomy[]>,
    // The location's explicit product ownership (empty for historic locations).
    selectedProducts: {
      type: Array,
      default: () => [],
    } as PropOptions<Taxonomy[]>,
  },
  data() {
    return {
      // Keyed by `${productId}:${treatmentId}`.
      selected: {} as Record<string, boolean>,
    };
  },
  watch: {
    // Rebuild whenever the product tree loads or the practice changes. The tree is
    // stable for the duration of an editing session, so internal state stays
    // authoritative once the user starts interacting.
    tree: {
      immediate: true,
      handler() {
        this.buildFromProps();
      },
    },
  },
  methods: {
    key(productId: number, treatmentId: number): string {
      return `${productId}:${treatmentId}`;
    },
    buildFromProps() {
      const treatmentIds = new Set(this.selectedTreatments.map((t) => t.id));
      const productIds = new Set(this.selectedProducts.map((p) => p.id));
      const hasExplicitProducts = productIds.size > 0;

      const next: Record<string, boolean> = {};
      this.tree.forEach((product) => {
        product.treatments.forEach((treatment) => {
          const treatmentChecked = treatmentIds.has(treatment.id);
          // Historic locations have no explicit products, so we pre-check a
          // treatment under every product it appears under (mirroring the legacy
          // ambiguity). Curated locations gate by their owned products.
          const productOwned = !hasExplicitProducts || productIds.has(product.id);
          if (treatmentChecked && productOwned) {
            next[this.key(product.id, treatment.id)] = true;
          }
        });
      });
      this.selected = next;

      // Silent upgrade: a historic location has treatments but no explicit product
      // ownership. Emit the reconstructed selection so the next save persists exact
      // ownership. No-op for already-curated (or empty) locations.
      if (!hasExplicitProducts && Object.keys(next).length > 0) {
        this.$nextTick(() => this.emitChange());
      }
    },
    isSelected(productId: number, treatmentId: number): boolean {
      return Boolean(this.selected[this.key(productId, treatmentId)]);
    },
    toggle(product: TreeProduct, treatment: TreeTreatment) {
      const k = this.key(product.id, treatment.id);
      const next = { ...this.selected };
      if (next[k]) {
        delete next[k];
      } else {
        next[k] = true;
      }
      this.selected = next;
      this.emitChange();
    },
    emitChange() {
      // Collapse the per-(product, treatment) selection into the distinct sets the
      // location stores: its treatments and its owned products.
      const treatmentTitles = new Map<number, string>();
      const productTitles = new Map<number, string>();

      this.tree.forEach((product) => {
        product.treatments.forEach((treatment) => {
          if (this.selected[this.key(product.id, treatment.id)]) {
            treatmentTitles.set(treatment.id, treatment.title);
            productTitles.set(product.id, product.title);
          }
        });
      });

      const treatments = Array.from(treatmentTitles, ([id, title]) => ({ id, title }));
      const products = Array.from(productTitles, ([id, title]) => ({ id, title }));
      this.$emit('change', { treatments, products });
    },
  },
});
